import { useCallback, useMemo } from 'react';

import { logger } from '@zep/core-logger';

import { useBuildAiQuizByYoutube } from '@/api/ai/useBuildAiQuiz';
import { type YoutubeVideoData } from '@/api/ai/youtube/types';
import { quizLevelToApiQuizDifficulty } from '@/shared/features/ai-quiz-generation/constants';
import { type BuildPromptOptions } from '@/shared/features/ai-quiz-generation/types';

import { useOpenYoutubeUrlModal } from '../../../modal/YoutubeUrlModal';
import {
  AI_GENERATION_EVENT_KEY,
  getPromptData,
  getStandardCodeData,
} from '../utils/getAiEventData';

import { type AchievementStandard, type UseAiByMethodParams } from './types';
import { useBuildAiYoutubeErrorAlert } from './useBuildAiYoutubeErrorAlert';

/**
 * YouTube 방식 AI 퀴즈 생성 커스텀 훅
 *
 * YouTube 영상을 첨부하여 퀴즈를 생성하는 YouTube 생성 방식을 위한
 * UseAiByMethodReturn 인터페이스 구현체
 *
 * 주요 기능:
 * - overlay-kit Promise 패턴으로 YoutubeUrlModal 호출 및 데이터 수신
 * - YouTube 영상 첨부 및 제거 기능
 * - React Query mutation으로 POST /quiz-builder/generate/youtube API 호출
 * - 콜백을 통해 성공/오류 처리를 상위 컴포넌트에 위임
 * - Query/File 방식과의 일관성을 위한 공통 인터페이스 구현
 *
 * @param params - onGenerate, onError 콜백을 포함한 훅 파라미터
 * @returns UseAiByMethodReturn 인터페이스 구현
 */
export const useAiByYoutube = (params: UseAiByMethodParams) => {
  const {
    onGenerateSuccess,
    onGenerateStart,
    onGenerateError,
    onAttach,
    onDetach,
    tryCount,
    generationId,
  } = params;

  // API 클라이언트의 기존 mutation 사용
  const { mutateAsync } = useBuildAiQuizByYoutube();

  // YoutubeUrlModal을 여는 훅
  const { openYoutubeUrlModal } = useOpenYoutubeUrlModal();

  // 임시
  const youtubeErrorAlert = useBuildAiYoutubeErrorAlert();

  /**
   * YouTube 영상 첨부 처리
   * overlay-kit Promise 패턴으로 YoutubeUrlModal을 열고 영상 데이터를 수신
   */
  const handleAttach = useCallback(
    async (initialVideoData?: YoutubeVideoData) => {
      // YoutubeUrlModal 열기
      const result = await openYoutubeUrlModal(initialVideoData);
      if (result) {
        onAttach(result);
      }
    },
    [openYoutubeUrlModal, onAttach],
  );

  /**
   * YouTube 영상 첨부 해제 처리
   * 첨부된 영상을 제거하고 상태를 초기화
   */
  const handleDetach = useCallback(() => {
    onDetach();
  }, [onDetach]);

  /**
   * YouTube 영상으로부터 퀴즈 생성 처리
   * 영상 존재 여부를 확인하고, API mutation을 호출하며, 적절한 콜백을 실행
   */
  const handleGenerate = useCallback(
    async (
      attachment: YoutubeVideoData,
      options: BuildPromptOptions,
      standards: AchievementStandard[],
      userPrompt?: string,
      // omit (undefined) 시 서버 기본 모델 결정. mutation factory 가 snake_case 변환.
      generationMode?: 'fast' | 'thorough',
      // Stage 1 UI cohort. wire 시 ui_cohort 로 변환. dropdown 일 때만
      // 서버가 Stage 2 backend_cohort (real/placebo) 를 평가한다.
      uiCohort?: 'as_is' | 'dropdown',
    ) => {
      // YouTube 영상 첨부 여부 확인
      onGenerateStart(tryCount > 0 ? true : false);

      const startTime = Date.now();

      try {
        const response = await mutateAsync({
          youtubeVideoId: attachment.videoId,
          type: options.quizType,
          difficulty: quizLevelToApiQuizDifficulty(options.quizLevel),
          quizCount: options.quizCount,
          tryCount, // 초기 시도
          locale: options.language,
          userPrompt,
          selectedStandardCodes: standards.map(standard => standard.code),
          generationId,
          generationMode,
          uiCohort,
        });
        // 생성된 퀴즈 아이템과 함께 상위 컴포넌트의 onGenerate 콜백 호출
        onGenerateSuccess(response.questions);
        const completedEventPayload = {
          ...options,
          ...getPromptData(
            {
              type: 'youtube' as const,
              data: attachment,
            },
            userPrompt || '',
          ),
          ...getStandardCodeData(standards),
          is_retry: tryCount > 0,
          requested_question_count: options.quizCount,
          sample_question: response.questions[0].question,
          sample_answer: response.questions[0].answer,
          sample_options: response.questions[0].options,
          sample_type: response.questions[0].type,
          latency_ms: Date.now() - startTime,
          generation_id: response.generationId || generationId,
        };
        logger.track(AI_GENERATION_EVENT_KEY, completedEventPayload);
        logger.track('biz_ai_generation_completed', completedEventPayload);
      } catch (error: unknown) {
        // API 실패 시 상위 컴포넌트의 onError 콜백 호출
        if (!(error instanceof Error)) {
          logger.captureError(error, {
            fingerprint: 'youtube_generate_error',
          });
        }
        logger.track('biz_ai_generation_failed', {
          generation_id: generationId,
          error_type: error instanceof Error ? error.name : 'unknown',
          attempt_no: tryCount,
          latency_ms: Date.now() - startTime,
          source_type: 'youtube',
        });
        logger.track(
          tryCount > 0
            ? 'ai_문제생성_유튜브_다시생성_에러'
            : 'ai_문제생성_유튜브_시작_에러',
        );
        onGenerateError();
        youtubeErrorAlert(error as Error);
      }
    },
    [
      onGenerateSuccess,
      onGenerateError,
      youtubeErrorAlert,
      tryCount,
      onGenerateStart,
      mutateAsync,
      generationId,
    ],
  );

  // 불필요한 리렌더링 방지를 위해 반환 객체 메모이제이션
  return useMemo(
    () => ({
      handleAttach,
      handleDetach,
      handleGenerate,
    }),
    [handleAttach, handleDetach, handleGenerate],
  );
};
